<!-- manage-product.component.html -->
<mat-tab-group>
  <!-- ===========================
       TAB 1: MANAGE PRODUCTS
  ============================ -->
  <mat-tab label="Manage Products">
    <div class="tab-section">

      <!-- Add Product -->
      <button mat-raised-button color="primary" (click)="openAddProductDialog()">
        + Add Product
      </button>

      <!-- Search -->
      <mat-form-field appearance="outline" class="search-field">
        <mat-label>Search Products</mat-label>
        <input
          matInput
          placeholder="Enter product name or SKU"
          [(ngModel)]="searchText"
          (ngModelChange)="onSearchTextChange()"
        >
      </mat-form-field>

      <!-- Assign to Group -->
      <button
        mat-raised-button
        color="primary"
        (click)="openAssignToGroupDialog()"
        [disabled]="selectedProductIds.length === 0"
      >
        Assign Selected to Group
      </button>

      <!-- Product Table -->
      <table mat-table [dataSource]="productStore.filteredProducts()" class="mat-elevation-z8">

        <!-- Select Checkbox -->
        <ng-container matColumnDef="select">
          <th mat-header-cell *matHeaderCellDef>
            <mat-checkbox
              (change)="toggleSelectAll($event)"
              [checked]="isAllSelected()"
            ></mat-checkbox>
          </th>
          <td mat-cell *matCellDef="let product">
            <mat-checkbox
              (click)="$event.stopPropagation()"
              (change)="toggleProductSelection(product.product_id)"
              [checked]="selectedProductIds.includes(product.product_id)"
            ></mat-checkbox>
          </td>
        </ng-container>

        <!-- SKU -->
        <ng-container matColumnDef="SKU">
          <th mat-header-cell *matHeaderCellDef (click)="onSort('product_id')" class="sortable-header">
            SKU
            <mat-icon>
  {{ productStore.sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
</mat-icon>

          </th>
          <td mat-cell *matCellDef="let product">
            {{ product.product_id }}
          </td>
        </ng-container>

        <!-- Name -->
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef (click)="onSort('name')" class="sortable-header">
            Name
            <mat-icon>
  {{ productStore.sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
</mat-icon>

          </th>
          <td mat-cell *matCellDef="let product">
            {{ product.name }}
          </td>
        </ng-container>

        <!-- Price -->
        <ng-container matColumnDef="price">
          <th mat-header-cell *matHeaderCellDef (click)="onSort('price')" class="sortable-header">
            Price
            <mat-icon>
  {{ productStore.sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
</mat-icon>

          </th>
          <td mat-cell *matCellDef="let product">
            {{ product.price }}
          </td>
        </ng-container>

        <!-- Groups -->
        <ng-container matColumnDef="groups">
          <th mat-header-cell *matHeaderCellDef>Groups</th>
          <td mat-cell *matCellDef="let product">
            {{ getGroupNames(product.groups) }}
          </td>
        </ng-container>

        <!-- Actions -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>

          <td mat-cell *matCellDef="let product">

            <!-- Edit -->
            <button
              mat-button
              color="accent"
              (click)="openEditProductDialog(product, $event)"
              [disabled]="product.deleted"
            >
              Edit
            </button>

            <!-- Delete or Restore -->
            <ng-container *ngIf="!product.deleted; else restoreBlock">
              <button
                mat-button color="warn"
                (click)="openDeleteProductDialog(product, $event)"
              >
                Delete
              </button>
            </ng-container>

            <ng-template #restoreBlock>
              <button mat-button (click)="restoreProduct(product)">Restore</button>
            </ng-template>

          </td>
        </ng-container>

        <!-- Sale -->
        <ng-container matColumnDef="sale">
          <th mat-header-cell *matHeaderCellDef>Sale</th>
          <td mat-cell *matCellDef="let product">
            <ng-container *ngIf="product.is_on_sale">
              ${{ product.sale_price }}
            </ng-container>
            <ng-container *ngIf="!product.is_on_sale">
              —
            </ng-container>
          </td>
        </ng-container>

        <!-- Header + Row -->
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>

        <tr
          mat-row
          *matRowDef="let row; columns: displayedColumns"
          [class.deleted-row]="row.deleted"
          (click)="goToProductDetail(row)"
          class="clickable-row"
          @fadeAnimation
        ></tr>

      </table>

      <!-- Messages -->
      <div *ngIf="productStore.filteredProducts().length === 0 && !searchText.trim()" class="no-results-message">
        No products available.
      </div>

      <div *ngIf="productStore.filteredProducts().length === 0 && searchText.trim()" class="no-results-message">
        No matching products found.
      </div>

      <!-- Pagination -->
      <div class="pagination-controls" *ngIf="!searchText.trim()">
        <div class="pagination-center">
          <button mat-button (click)="prevPage()" [disabled]="currentPage === 0">
            Previous
          </button>

          <span class="page-number">Page {{ currentPage + 1 }}</span>

          <button mat-button (click)="nextPage()">
            Next
          </button>
        </div>

        <!-- Page Size -->
        <div class="pagination-right">
          <mat-form-field appearance="outline" class="page-size-selector">
            <mat-label>Prods/page</mat-label>
            <mat-select [(ngModel)]="pageSize" (selectionChange)="onPageSizeChange($event.value)">
              <mat-option *ngFor="let size of pageSizes" [value]="size">
                {{ size }}
              </mat-option>
            </mat-select>
          </mat-form-field>
        </div>
      </div>

    </div>
  </mat-tab>


  <!-- ===========================
       TAB 2: LINK PRODUCTS → CATEGORIES
  ============================ -->
  <mat-tab label="Link Products to Categories">
    <div class="tab-section">

      <!-- Select Product -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Select Product</mat-label>
        <mat-select
          [(ngModel)]="selectedProductId"
          (selectionChange)="onProductSelectionChange()"
        >
          <mat-option *ngFor="let p of productStore.fullProducts()" [value]="p.product_id">
            {{ p.name }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Select Categories -->
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>Select Categories</mat-label>
        <mat-select [(ngModel)]="selectedCategoryIds" multiple>
          <mat-option *ngFor="let c of categories" [value]="c.category_id">
            {{ c.name }}
          </mat-option>
        </mat-select>
      </mat-form-field>

      <!-- Linked Categories -->
      <div *ngIf="linkedCategories.length > 0" class="linked-categories-section">
        <h4>Linked Categories:</h4>

        <table mat-table [dataSource]="linkedCategories" class="mat-elevation-z8">

          <!-- Category -->
          <ng-container matColumnDef="category_name">
            <th mat-header-cell *matHeaderCellDef>Category</th>
            <td mat-cell *matCellDef="let cat">{{ cat.name }}</td>
          </ng-container>

          <!-- Unlink -->
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let cat">
              <button mat-button color="warn" (click)="confirmUnlinkCategory(cat.category_id)">
                Unlink
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="['category_name','actions']"></tr>
          <tr mat-row *matRowDef="let row; columns: ['category_name','actions']"></tr>

        </table>
      </div>

      <!-- Link Button -->
      <button
        mat-raised-button
        color="primary"
        (click)="linkProductToCategories()"
        [disabled]="!selectedProductId || selectedCategoryIds.length === 0"
      >
        Link Product to Categories
      </button>

      <div *ngIf="linkSuccess" class="link-success-message">
        Categories linked successfully!
      </div>

    </div>
  </mat-tab>
</mat-tab-group>
